import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Dialog } from '@mui/material';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { IconX } from '@tabler/icons-react';

import { AlertMessage, Button, IconButton, Loader } from 'ui';
import { getNetworkError } from 'common/utils';

import SubscriptionLayout from './SubscriptionLayout';
import { ONBOARDING_STEPS } from '../../core/constants';
import useInterval from '../../hooks/useInterval';
import InvoicesTable from '../subscription/InvoicesTable';
import { getInvoices as getInvoicesApi, createCheckoutSetupSession } from '../../api';
import appSettings from '../../app-settings';
import { useAccount } from '../../store/account/hooks';
import { isFuture } from 'date-fns';
import { waitFor } from '../../core/utils';

const stripePromise = loadStripe(appSettings.stripePublishableKey);

const InvoicesStep = ({ setStep }) => {
  const {
    account: { subscription, bypassPaymentMethodRequirement },
    getAccount,
  } = useAccount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const isFutureSubscription = subscription?.startDate && isFuture(new Date(subscription.startDate * 1000));
  const needsPaymentMethod =
    !bypassPaymentMethodRequirement && subscription?.collectionMethod !== 'charge_automatically';

  const getInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInvoicesApi();
      setInvoices(data);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setLoading(false);
  }, []);

  const getCheckoutSessionClientSecret = async () => {
    try {
      const data = await createCheckoutSetupSession();
      return data?.clientSecret;
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
      return;
    }
  };

  useEffect(() => {
    getInvoices();
  }, [getInvoices]);

  // poll for account updates every 30 seconds if payment method is needed
  // this handles the case where a user pays an invoice (which saves their payment method)
  useInterval(
    () => {
      getAccount();
      getInvoices();
    },
    needsPaymentMethod ? 30000 : null,
  );

  const handleAddPaymentMethod = async () => {
    setCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
  };

  const handleCheckoutComplete = async () => {
    await waitFor(2000); // Give time for embedded success message to display
    setCheckoutComplete(true);
    handleCheckoutClose();
  };

  return (
    <SubscriptionLayout>
      <div className="relative flex size-full flex-col items-center justify-center px-8">
        <div className="space-y-8">
          <div className="space-y-4">
            <h1 className="text-h2 font-semibold">Subscription</h1>
            {isFutureSubscription ? (
              <p className="text-lg">
                Your DonorSpring subscription is scheduled to start on{' '}
                <strong>{new Date(subscription.startDate * 1000).toLocaleDateString()}</strong>! Your upcoming
                invoice(s) are listed below.
              </p>
            ) : (
              <p className="text-lg">
                Your DonorSpring subscription is active! You can continue with account setup or view and pay your
                subscription invoice(s) below.
              </p>
            )}
          </div>

          <div className="relative flex-1 overflow-x-auto overflow-y-hidden">
            <InvoicesTable data={invoices} />
            {loading && <Loader />}
          </div>

          {needsPaymentMethod && !checkoutComplete ? (
            isFutureSubscription ? (
              <>
                <p className="text-lg">
                  You may continue setting up your DonorSpring account before your subscription start date, but we
                  require a payment method on file. We will automatically charge your payment method on your
                  subscription start date.
                </p>
                <div className="mx-auto flex max-w-[336px]">
                  <Button
                    title="Add Payment Method"
                    color="primary"
                    size="xl"
                    className="w-full"
                    onClick={handleAddPaymentMethod}
                  />
                </div>
              </>
            ) : (
              <p className="text-lg">
                We require a paid subscription invoice before you can continue setting up your DonorSpring account.
                Please use the &quot;View or Pay&quot; button above to pay any outstanding invoices.
              </p>
            )
          ) : (
            <div className="mx-auto flex max-w-[336px]">
              <Button
                title="Continue"
                color="primary"
                size="xl"
                className="w-full"
                onClick={() => setStep(ONBOARDING_STEPS.accountInfo)}
              />
            </div>
          )}
        </div>
      </div>

      <Dialog open={checkoutOpen} onClose={handleCheckoutClose} maxWidth="sm">
        <div className="relative p-6">
          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleCheckoutClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              fetchClientSecret: getCheckoutSessionClientSecret,
              onComplete: () => handleCheckoutComplete(),
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </Dialog>

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </SubscriptionLayout>
  );
};

InvoicesStep.propTypes = {
  setStep: PropTypes.func.isRequired,
};

export default InvoicesStep;
